import type {
    ChangeEvent,
    FC,
    FormEvent,
    ReactNode
} from 'react'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import {
    ChevronDown,
    CircleAlert,
    CircleCheck,
    LoaderCircle
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { PillArrow, pillClassName } from '@/components/generals/pill-link'

import { enquiryServiceOptions } from '@/constants/home'

type FieldName = 'name' | 'company' | 'email' | 'phone' | 'service' | 'origin' | 'destination' | 'message'

type FormValues = Record<FieldName, string>

type FormErrors = Partial<Record<FieldName, string>>

type Status = 'idle' | 'submitting' | 'success' | 'error'

const EMPTY_VALUES: FormValues = {
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    origin: '',
    destination: '',
    message: ''
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const inputClassName = 'w-full rounded-xl border border-input bg-white px-4 text-base text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 hover:border-ink/40 focus:border-ink focus-visible:ring-2 focus-visible:ring-brand/25 aria-[invalid=true]:border-destructive'

const validate = (values: FormValues): FormErrors => {
    const errors: FormErrors = {}

    if (!values.name.trim()) errors.name = "Informe seu nome."
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = "Informe um e-mail válido."
    if (values.message.trim().length < 10) errors.message = "Conte um pouco mais sobre a sua operação."

    return errors
}

interface FieldProps {
    id: string
    label: string
    required?: boolean
    error?: string
    className?: string
    children: ReactNode
}

const Field: FC<FieldProps> = ({ id, label, required = false, error, className, children }): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <label htmlFor={id} className='font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground'>
                {label}
                {required && <span aria-hidden='true' className='text-brand'> *</span>}
            </label>

            {children}

            {error && (
                <p id={`${id}-error`} className='flex items-center gap-1.5 text-sm text-destructive'>
                    <CircleAlert aria-hidden='true' className='size-3.5 shrink-0' />
                    {error}
                </p>
            )}
        </div>
    )
}

export const EnquiryForm: FC = (): ReactNode => {
    const router = useRouter()
    const [values, setValues] = useState<FormValues>(EMPTY_VALUES)
    const [errors, setErrors] = useState<FormErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [notice, setNotice] = useState('')
    const [honeypot, setHoneypot] = useState('')
    const [submittedEmail, setSubmittedEmail] = useState('')

    // Preselect the service when the visitor arrives from a service link (/contato?servico=...).
    useEffect(() => {
        if (!router.isReady) return

        const requested = typeof router.query.servico === 'string' ? router.query.servico : ''

        if (requested && enquiryServiceOptions.includes(requested)) {
            setValues((current) => (current.service ? current : { ...current, service: requested }))
        }
    }, [router.isReady, router.query.servico])

    const onChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        const name = event.target.name as FieldName
        const { value } = event.target

        setValues((current) => ({ ...current, [name]: value }))

        if (errors[name]) {
            setErrors((current) => ({ ...current, [name]: undefined }))
        }
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const nextErrors = validate(values)

        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors)
            setStatus('error')
            setNotice("Verifique os campos destacados.")
            document.getElementById(`enquiry-${Object.keys(nextErrors)[0]}`)?.focus()
            return
        }

        setErrors({})
        setStatus('submitting')
        setNotice('')

        try {
            const response = await fetch('/api/enquiries', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, website: honeypot })
            })

            const data = (await response.json().catch(() => null)) as { ok?: boolean, message?: string, fields?: FormErrors } | null

            if (!response.ok || !data?.ok) {
                if (data?.fields) setErrors(data.fields)

                setStatus('error')
                setNotice(data?.message ?? "Não foi possível enviar sua mensagem agora. Tente novamente.")
                return
            }

            setSubmittedEmail(values.email.trim())
            setValues(EMPTY_VALUES)
            setStatus('success')
        }
        catch {
            setStatus('error')
            setNotice("Não conseguimos conectar ao servidor. Verifique sua conexão e tente novamente.")
        }
    }

    const describedBy = (name: FieldName): string | undefined => (errors[name] ? `enquiry-${name}-error` : undefined)

    if (status === 'success') {
        return (
            <div role='status' className='rounded-3xl bg-ink p-8 text-white animate-in fade-in zoom-in-95 duration-500 md:p-12'>
                <CircleCheck aria-hidden='true' className='size-10 text-brand-soft' strokeWidth={1.5} />

                <h2 className='mt-8 font-heading text-2xl uppercase leading-tight tracking-[-0.01em] md:text-3xl'>
                    Obrigado, recebemos sua mensagem.
                </h2>

                <p className='mt-4 max-w-md leading-relaxed text-white/70'>
                    Nossos especialistas vão analisar sua solicitação e responder para {submittedEmail} em breve. Se preferir, fale com a gente pelo telefone ou WhatsApp.
                </p>

                <button
                    type='button'
                    onClick={() => {
                        setStatus('idle')
                        setNotice('')
                    }}
                    className={pillClassName('outline-light', 'md', 'mt-10')}
                >
                    Enviar outra mensagem
                </button>
            </div>
        )
    }

    return (
        <form noValidate onSubmit={onSubmit} className='relative' aria-describedby='enquiry-required-note'>
            <div aria-hidden='true' className='absolute -left-[9999px] top-0 h-px w-px overflow-hidden'>
                <label htmlFor='enquiry-website'>Deixe este campo vazio</label>
                <input
                    id='enquiry-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={honeypot}
                    onChange={(event) => setHoneypot(event.target.value)}
                />
            </div>

            <div className='grid gap-6 md:grid-cols-2'>
                <Field id='enquiry-name' label="Nome completo" required error={errors.name}>
                    <input
                        id='enquiry-name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        aria-required='true'
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={describedBy('name')}
                        value={values.name}
                        onChange={onChange}
                        className={cn(inputClassName, 'h-12')}
                    />
                </Field>

                <Field id='enquiry-company' label="Empresa" error={errors.company}>
                    <input
                        id='enquiry-company'
                        name='company'
                        type='text'
                        autoComplete='organization'
                        aria-invalid={Boolean(errors.company)}
                        aria-describedby={describedBy('company')}
                        value={values.company}
                        onChange={onChange}
                        className={cn(inputClassName, 'h-12')}
                    />
                </Field>

                <Field id='enquiry-email' label="E-mail" required error={errors.email}>
                    <input
                        id='enquiry-email'
                        name='email'
                        type='email'
                        autoComplete='email'
                        aria-required='true'
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={describedBy('email')}
                        value={values.email}
                        onChange={onChange}
                        className={cn(inputClassName, 'h-12')}
                    />
                </Field>

                <Field id='enquiry-phone' label="Telefone" error={errors.phone}>
                    <input
                        id='enquiry-phone'
                        name='phone'
                        type='tel'
                        autoComplete='tel'
                        aria-invalid={Boolean(errors.phone)}
                        aria-describedby={describedBy('phone')}
                        value={values.phone}
                        onChange={onChange}
                        className={cn(inputClassName, 'h-12')}
                    />
                </Field>

                <Field id='enquiry-service' label="Serviço de interesse" error={errors.service} className='md:col-span-2'>
                    <div className='relative'>
                        <select
                            id='enquiry-service'
                            name='service'
                            aria-invalid={Boolean(errors.service)}
                            aria-describedby={describedBy('service')}
                            value={values.service}
                            onChange={onChange}
                            className={cn(inputClassName, 'h-12 cursor-pointer appearance-none pr-12')}
                        >
                            <option value=''>Escolha um serviço</option>
                            {enquiryServiceOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>

                        <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
                    </div>
                </Field>

                <Field id='enquiry-origin' label="Origem da carga" error={errors.origin}>
                    <input
                        id='enquiry-origin'
                        name='origin'
                        type='text'
                        placeholder="Cidade ou porto"
                        aria-invalid={Boolean(errors.origin)}
                        aria-describedby={describedBy('origin')}
                        value={values.origin}
                        onChange={onChange}
                        className={cn(inputClassName, 'h-12')}
                    />
                </Field>

                <Field id='enquiry-destination' label="Destino da carga" error={errors.destination}>
                    <input
                        id='enquiry-destination'
                        name='destination'
                        type='text'
                        placeholder="Cidade ou porto"
                        aria-invalid={Boolean(errors.destination)}
                        aria-describedby={describedBy('destination')}
                        value={values.destination}
                        onChange={onChange}
                        className={cn(inputClassName, 'h-12')}
                    />
                </Field>

                <Field id='enquiry-message' label="Mensagem" required error={errors.message} className='md:col-span-2'>
                    <textarea
                        id='enquiry-message'
                        name='message'
                        rows={6}
                        placeholder="Conte sobre a sua operação: o que você importa ou exporta, volumes e prazos."
                        aria-required='true'
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={describedBy('message')}
                        value={values.message}
                        onChange={onChange}
                        className={cn(inputClassName, 'resize-y py-3 leading-relaxed')}
                    />
                </Field>
            </div>

            {status === 'error' && notice && (
                <p role='alert' className='mt-6 flex items-start gap-2 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive'>
                    <CircleAlert aria-hidden='true' className='mt-0.5 size-4 shrink-0' />
                    {notice}
                </p>
            )}

            <div className='mt-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between'>
                <p id='enquiry-required-note' className='text-sm text-muted-foreground'>
                    Campos marcados com * são obrigatórios. Usamos seus dados apenas para responder a esta mensagem.
                </p>

                <button
                    type='submit'
                    disabled={status === 'submitting'}
                    className={pillClassName('dark', 'lg')}
                >
                    {status === 'submitting' ? (
                        <>
                            Enviando
                            <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />
                        </>
                    ) : (
                        <>
                            Enviar mensagem
                            <PillArrow />
                        </>
                    )}
                </button>
            </div>
        </form>
    )
}
